
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { WorkoutFormValues } from "../schema/workoutSchema";
import { useTranslations } from "@/hooks/useTranslations";

interface BasicInfoFieldsProps {
  form: UseFormReturn<WorkoutFormValues>;
  students?: any[];
}

export function BasicInfoFields({ form, students }: BasicInfoFieldsProps) {
  const { t } = useTranslations("workouts");
  
  return (
    <div className="bg-gray-50/80 backdrop-blur-sm p-4 sm:p-6 rounded-lg space-y-6 dark:bg-gray-800/70 dark:border dark:border-gray-700/50">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="target_student_id"
          render={({ field }) => (
            <FormItem className="col-span-full sm:col-span-1">
              <FormLabel className="dark:text-white">{t("form.student")} <span className="text-red-500">*</span></FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600">
                    <SelectValue placeholder={t("form.selectStudent")} className="dark:text-gray-300" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white dark:bg-gray-700 dark:border-gray-600 z-50">
                  {students?.map(student => (
                    <SelectItem key={student.id} value={student.id} className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">
                      {student.first_name} {student.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="dark:text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem className="col-span-full sm:col-span-1">
              <FormLabel className="dark:text-white">{t("form.title")}</FormLabel>
              <FormControl>
                <Input {...field} className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600" />
              </FormControl>
              <FormMessage className="dark:text-red-400" />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="dark:text-white">{t("form.workoutType")} <span className="text-red-500">*</span></FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600">
                    <SelectValue placeholder={t("form.selectType")} className="dark:text-gray-300" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white dark:bg-gray-700 dark:border-gray-600 z-50">
                  <SelectItem value="musculacao" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("form.types.strength")}</SelectItem>
                  <SelectItem value="funcional" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("form.types.functional")}</SelectItem>
                  <SelectItem value="cardio" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("form.types.cardio")}</SelectItem>
                  <SelectItem value="hiit" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("form.types.hiit")}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage className="dark:text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="difficulty_level"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="dark:text-white">{t("form.difficultyLevel")} <span className="text-red-500">*</span></FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600">
                    <SelectValue placeholder={t("form.selectLevel")} className="dark:text-gray-300" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white dark:bg-gray-700 dark:border-gray-600 z-50">
                  <SelectItem value="iniciante" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("form.levels.beginner")}</SelectItem>
                  <SelectItem value="intermediario" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("form.levels.intermediate")}</SelectItem>
                  <SelectItem value="avancado" className="dark:text-white dark:focus:bg-gray-600 dark:focus:text-white">{t("form.levels.advanced")}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage className="dark:text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="duration"
          render={({ field: { onChange, ...field } }) => (
            <FormItem>
              <FormLabel className="dark:text-white">{t("form.duration")}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min="1"
                  onChange={e => onChange(Number(e.target.value))}
                  {...field}
                  className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                />
              </FormControl>
              <FormMessage className="dark:text-red-400" />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-6">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="dark:text-white">{t("form.description")}</FormLabel>
              <FormControl>
                <Textarea {...field} className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600" />
              </FormControl>
              <FormMessage className="dark:text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="dark:text-white">{t("form.additionalNotes")}</FormLabel>
              <FormControl>
                <Textarea {...field} className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600" />
              </FormControl>
              <FormMessage className="dark:text-red-400" />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
